import Phaser from 'phaser';
import { MAX_STACK_SLOTS, SLOT_SIZE, getStackY, GAME_WIDTH, FONT_FAMILY } from '../config/constants';
import { crispText } from './textUtils';
import { getGameplayColorHex } from '../config/colors';
import { type AttackerData, primaryColor, totalAmmo } from '../entities/Attacker';

export class StackUI {
  private scene: Phaser.Scene;
  private slotBgs: Phaser.GameObjects.Image[] = [];
  private slotStartX: number;
  private slotSpacing: number;
  private stackY: number;
  private readonly capacity: number;

  // ── Pooled overlay objects ──────────────────────────────────────────
  // Pre-created pools for slot fill graphics and ammo labels.
  // Instead of destroying/recreating each update, we hide unused ones.

  /** Pool of Graphics objects for slot color fills (single + multi connecting bars + per-orb fills). */
  private fillPool: Phaser.GameObjects.Graphics[] = [];
  /** Pool of Text objects for ammo labels. */
  private labelPool: Phaser.GameObjects.Text[] = [];
  /** How many fill pool objects are currently active. */
  private activeFills = 0;
  /** How many label pool objects are currently active. */
  private activeLabels = 0;

  // Max pool sizes: 5 slots can produce at most:
  // - Single: 1 fill + 1 label per slot = 5 fills + 5 labels
  // - Multi (worst case: 1 triple + 1 double): bar + 3 orb fills + 3 labels + bar + 2 orb fills + 2 labels
  // Conservative max: 15 fills, 10 labels
  private static readonly MAX_FILLS = 15;
  private static readonly MAX_LABELS = 10;

  // Current waiting slot data (for hit detection on multi-slot attackers)
  private currentSlots: readonly (AttackerData | null)[] = [];

  // Track previous attacker positions for compaction animation
  private prevAttackerSlotIdx = new Map<number, number>();
  // Active compaction offsets: attackerId → tweened offset object
  private compactOffsets = new Map<number, { value: number }>();

  // Dirty-check fingerprint: "id:ammo,id:ammo,..." for each slot
  private lastFingerprint = 0;

  constructor(scene: Phaser.Scene, capacity: number = MAX_STACK_SLOTS) {
    this.scene = scene;
    this.capacity = capacity;
    this.stackY = getStackY();
    this.slotSpacing = SLOT_SIZE + 12;
    const totalWidth = this.capacity * this.slotSpacing;
    this.slotStartX = (GAME_WIDTH - totalWidth) / 2 + SLOT_SIZE / 2;

    crispText(scene.add.text(GAME_WIDTH / 2, this.stackY - 64, 'Waiting', {
      fontFamily: FONT_FAMILY,
      fontSize: '28px',
      color: '#6666aa',
    })).setOrigin(0.5);

    // Draw glass slot backgrounds
    for (let i = 0; i < this.capacity; i++) {
      const x = this.slotStartX + i * this.slotSpacing;
      const slot = scene.add.image(x, this.stackY, 'slot-bg');
      slot.setDisplaySize(SLOT_SIZE, SLOT_SIZE);
      this.slotBgs.push(slot);
    }

    // Pre-create fill graphics pool
    for (let i = 0; i < StackUI.MAX_FILLS; i++) {
      const g = scene.add.graphics();
      g.setVisible(false);
      this.fillPool.push(g);
    }

    // Pre-create label text pool
    for (let i = 0; i < StackUI.MAX_LABELS; i++) {
      const t = crispText(scene.add.text(0, 0, '', {
        fontFamily: FONT_FAMILY,
        fontSize: '34px',
        color: '#ffffff',
        fontStyle: 'bold',
        stroke: '#000000',
        strokeThickness: 4,
      })).setOrigin(0.5).setVisible(false);
      this.labelPool.push(t);
    }
  }

  /** Acquire a Graphics object from the pool, clearing it for reuse. */
  private acquireFill(): Phaser.GameObjects.Graphics {
    if (this.activeFills < this.fillPool.length) {
      const g = this.fillPool[this.activeFills++];
      g.clear();
      g.setVisible(true);
      g.setAlpha(1);
      return g;
    }
    // Pool exhausted — create a new one (shouldn't happen with correct max)
    const g = this.scene.add.graphics();
    this.fillPool.push(g);
    this.activeFills++;
    return g;
  }

  /** Acquire a Text object from the pool, resetting it for reuse. */
  private acquireLabel(): Phaser.GameObjects.Text {
    if (this.activeLabels < this.labelPool.length) {
      const t = this.labelPool[this.activeLabels++];
      t.setVisible(true);
      t.setAlpha(1);
      return t;
    }
    // Pool exhausted — create a new one
    const t = crispText(this.scene.add.text(0, 0, '', {
      fontFamily: FONT_FAMILY,
      fontSize: '34px',
      color: '#ffffff',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 4,
    })).setOrigin(0.5);
    this.labelPool.push(t);
    this.activeLabels++;
    return t;
  }

  /** Hide all pooled objects beyond the active count. */
  private hideUnused(): void {
    for (let i = this.activeFills; i < this.fillPool.length; i++) {
      this.fillPool[i].setVisible(false);
    }
    for (let i = this.activeLabels; i < this.labelPool.length; i++) {
      this.labelPool[i].setVisible(false);
    }
  }

  update(slots: readonly (AttackerData | null)[]): void {
    this.currentSlots = slots;

    // Build a numeric fingerprint of slot contents to skip expensive redraws when nothing changed.
    // Uses a simple hash combining attacker id, ammo per orb, and mystery state.
    let fp = 0;
    for (let i = 0; i < this.capacity; i++) {
      const a = slots[i];
      if (a) {
        fp = (fp * 31 + a.id) | 0;
        fp = (fp * 31 + i) | 0;
        for (const orb of a.orbs) {
          fp = (fp * 31 + orb.ammo) | 0;
          if (orb.mystery) fp = (fp * 31 + 1) | 0;
        }
      }
      fp = (fp * 31 + 997) | 0; // slot separator
    }
    const hasActiveCompaction = this.compactOffsets.size > 0;
    if (fp === this.lastFingerprint && !hasActiveCompaction) return;
    this.lastFingerprint = fp;

    // Build current attacker → first slot index map
    const currentPositions = new Map<number, number>();
    for (let i = 0; i < this.capacity; i++) {
      const a = slots[i];
      if (a && !currentPositions.has(a.id)) {
        currentPositions.set(a.id, i);
      }
    }

    // Detect position changes and start compaction tweens
    for (const [id, newIdx] of currentPositions) {
      const prevIdx = this.prevAttackerSlotIdx.get(id);
      if (prevIdx !== undefined && prevIdx !== newIdx && !this.compactOffsets.has(id)) {
        const offset = { value: (prevIdx - newIdx) * this.slotSpacing };
        this.compactOffsets.set(id, offset);
        this.scene.tweens.add({
          targets: offset,
          value: 0,
          duration: 150,
          ease: 'Cubic.easeOut',
          onComplete: () => this.compactOffsets.delete(id),
        });
      }
    }

    // Clean up offsets for attackers that are gone
    for (const id of this.compactOffsets.keys()) {
      if (!currentPositions.has(id)) {
        this.compactOffsets.delete(id);
      }
    }

    // Save positions for next frame
    this.prevAttackerSlotIdx = currentPositions;

    // Reset pool counters (reuse from start)
    this.activeFills = 0;
    this.activeLabels = 0;

    // Reset all slot backgrounds
    for (let i = 0; i < this.capacity; i++) {
      this.slotBgs[i].setTint(0xffffff);
      this.slotBgs[i].setAlpha(0.5);
    }

    // Find attacker groups (contiguous runs of the same attacker)
    const seen = new Set<number>();
    for (let i = 0; i < this.capacity; i++) {
      const attacker = slots[i];
      if (!attacker || seen.has(attacker.id)) continue;
      seen.add(attacker.id);

      const startIdx = i;
      let endIdx = i;
      while (endIdx + 1 < this.capacity && slots[endIdx + 1] === attacker) {
        endIdx++;
      }
      const span = endIdx - startIdx + 1;

      // Check if all orbs are mystery
      const allMystery = attacker.orbs.every(o => o.mystery);
      const color = allMystery ? 0x555577 : getGameplayColorHex(primaryColor(attacker));

      // Get compaction animation offset for this attacker
      const offsetObj = this.compactOffsets.get(attacker.id);
      const xOffset = offsetObj ? offsetObj.value : 0;

      if (span === 1) {
        // Solid color fill matching multi-slot brightness
        const x = this.slotStartX + i * this.slotSpacing + xOffset;
        const isDepleted = totalAmmo(attacker) <= 0;
        const fill = this.acquireFill();
        fill.setDepth(1);
        fill.fillStyle(color, isDepleted ? 0.35 : 0.85);
        fill.fillRoundedRect(
          x - SLOT_SIZE / 2, this.stackY - SLOT_SIZE / 2,
          SLOT_SIZE, SLOT_SIZE, 12,
        );
        fill.lineStyle(3, 0xffffff, 0.3);
        fill.strokeRoundedRect(
          x - SLOT_SIZE / 2, this.stackY - SLOT_SIZE / 2,
          SLOT_SIZE, SLOT_SIZE, 12,
        );
        this.slotBgs[i].setAlpha(0);

        const labelText = allMystery ? '?' : `${totalAmmo(attacker)}`;
        const label = this.acquireLabel();
        label.setText(labelText);
        label.setPosition(x, this.stackY);
        label.setFontSize(34);
        label.setColor(allMystery ? '#ccccdd' : (isDepleted ? '#888888' : '#ffffff'));
        label.setDepth(2);
      } else {
        // Multi-slot: per-orb colored sections with connecting bar
        const x1 = this.slotStartX + startIdx * this.slotSpacing + xOffset;
        const x2 = this.slotStartX + endIdx * this.slotSpacing + xOffset;
        const centerX = (x1 + x2) / 2;
        const fullWidth = (x2 - x1) + SLOT_SIZE + 8;

        for (let j = startIdx; j <= endIdx; j++) {
          this.slotBgs[j].setAlpha(0);
        }

        // Dark connecting bar (shows linkage)
        const bar = this.acquireFill();
        bar.setDepth(1);
        bar.fillStyle(0x333366, 0.5);
        bar.fillRoundedRect(
          centerX - fullWidth / 2, this.stackY - SLOT_SIZE / 2 - 4,
          fullWidth, SLOT_SIZE + 8, 16,
        );
        bar.lineStyle(3, 0xffffff, 0.2);
        bar.strokeRoundedRect(
          centerX - fullWidth / 2, this.stackY - SLOT_SIZE / 2 - 4,
          fullWidth, SLOT_SIZE + 8, 16,
        );

        // Per-orb colored fills with individual ammo labels
        for (let j = startIdx; j <= endIdx; j++) {
          const orbIdx = j - startIdx;
          const orb = attacker.orbs[orbIdx];
          const isMystery = orb?.mystery ?? false;
          const orbColor = isMystery ? 0x555577 : getGameplayColorHex(orb?.colorIndex ?? primaryColor(attacker));
          const orbX = this.slotStartX + j * this.slotSpacing + xOffset;
          const orbDepleted = orb ? orb.ammo <= 0 : false;

          const fill = this.acquireFill();
          fill.setDepth(2);
          fill.fillStyle(orbColor, orbDepleted ? 0.35 : 0.85);
          fill.fillRoundedRect(
            orbX - SLOT_SIZE / 2 + 2, this.stackY - SLOT_SIZE / 2 + 2,
            SLOT_SIZE - 4, SLOT_SIZE - 4, 10,
          );
          fill.lineStyle(2, 0xffffff, 0.3);
          fill.strokeRoundedRect(
            orbX - SLOT_SIZE / 2 + 2, this.stackY - SLOT_SIZE / 2 + 2,
            SLOT_SIZE - 4, SLOT_SIZE - 4, 10,
          );

          // Per-orb ammo label
          const orbLabelText = isMystery ? '?' : `${orb ? orb.ammo : 0}`;
          const orbLabel = this.acquireLabel();
          orbLabel.setText(orbLabelText);
          orbLabel.setPosition(orbX, this.stackY);
          orbLabel.setFontSize(32);
          orbLabel.setColor(isMystery ? '#ccccdd' : (orbDepleted ? '#888888' : '#ffffff'));
          orbLabel.setDepth(3);
        }
      }
    }

    // Hide any unused pool objects
    this.hideUnused();
  }

  getSlotPosition(index: number): { x: number; y: number } {
    return { x: this.slotStartX + index * this.slotSpacing, y: this.stackY };
  }


  /** Flash a red outline around the entire waiting stack to warn it is full. */
  flashFull(): void {
    // Compute bounding box that encompasses all waiting slots
    const x1 = this.slotStartX - SLOT_SIZE / 2;
    const x2 = this.slotStartX + (this.capacity - 1) * this.slotSpacing + SLOT_SIZE / 2;
    const pad = 10; // padding around the slots
    const rectX = x1 - pad;
    const rectY = this.stackY - SLOT_SIZE / 2 - pad;
    const rectW = (x2 - x1) + pad * 2;
    const rectH = SLOT_SIZE + pad * 2;
    const cornerRadius = 20;

    const outline = this.scene.add.graphics().setDepth(50);
    outline.lineStyle(8, 0xff3333, 1);
    outline.strokeRoundedRect(rectX, rectY, rectW, rectH, cornerRadius);
    outline.setAlpha(0);

    // Blink 3 times: alpha 0→1→0→1→0→1→0 over ~1.5 seconds
    this.scene.tweens.add({
      targets: outline,
      alpha: { from: 0, to: 1 },
      duration: 200,
      yoyo: true,
      repeat: 2,
      ease: 'Sine.easeInOut',
      onComplete: () => {
        outline.destroy();
      },
    });
  }

  getSlotAt(x: number, y: number): number | null {
    if (Math.abs(y - this.stackY) >= SLOT_SIZE / 2) return null;

    const seen = new Set<number>();
    for (let i = 0; i < this.capacity; i++) {
      const attacker = this.currentSlots[i];
      if (!attacker || seen.has(attacker.id)) continue;
      seen.add(attacker.id);

      const startIdx = i;
      let endIdx = i;
      while (endIdx + 1 < this.capacity && this.currentSlots[endIdx + 1] === attacker) {
        endIdx++;
      }

      const x1 = this.slotStartX + startIdx * this.slotSpacing - SLOT_SIZE / 2;
      const x2 = this.slotStartX + endIdx * this.slotSpacing + SLOT_SIZE / 2;

      if (x >= x1 && x <= x2) {
        return startIdx;
      }
    }

    for (let i = 0; i < this.slotBgs.length; i++) {
      const slot = this.slotBgs[i];
      if (Math.abs(x - slot.x) < SLOT_SIZE / 2) {
        return i;
      }
    }
    return null;
  }
}
